import i18n from '@/i18n';

/**
 * 产品合规展示工具：Temu GLOBAL（productId / certType / repId）。
 * 兼容既有列表页对 agency*、reviewState 字段的读取。
 */

export const COMPLIANCE_GROUP_LABELS = {
  CERT: '资质证书',
  REP: '责任人/代表',
  LABEL: '合规标签',
  PHOTO: '实拍图',
  ZSZZL: '资质证书',
  GSL: '代理公司',
  HGXXL: '合规信息',
  SPTL: '卖点证书',
  SPSMS: '说明书',
};

/** agencyType：skc-agency-detail / save-skc-agency 一级类型 */
export const AGENCY_TYPE_LABELS = {
  0: '欧代',
  1: '英代',
  2: '美代',
  3: '生产制造商',
};

export const AGENCY_SUB_TYPE_LABELS = {
  20: '欧洲代理',
  21: '欧盟制造商',
  22: '欧盟进口商',
  23: '欧盟配送服务提供商',
};

export const AGENCY_APPLY_STATUS_LABELS = {
  0: '待补充',
  1: '待审核(可绑)',
  2: '申报成功',
  3: '审核失败',
};

export const AGENCY_STATUS_LABELS = {
  0: '生效中',
  1: '已过期',
  2: '未生效',
};

export const AGENCY_COVERED_RANGE_LABELS = {
  1: '全部商品',
  2: '部分商品',
};

export const AGENCY_ORIGIN_TYPE_LABELS = {
  0: '手动创建',
  1: '线上办理',
};

/**
 * requirements/list 条目 reviewState：
 * 0 缺失、1 待审核、2 成功、3 驳回
 */
export const REVIEW_STATE_LABELS = {
  0: '待补全',
  1: '待审核',
  2: '已通过',
  3: '已驳回',
};

/**
 * skc-agency-detail 绑定状态 reviewState：
 * 1 待绑定；2 待审核；3 绑定失败；4 绑定成功
 */
export const SKC_AGENCY_REVIEW_STATE_LABELS = {
  1: '待绑定',
  2: '待审核',
  3: '绑定失败',
  4: '绑定成功',
};

function complianceText(key, fallback) {
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback });
}

export function complianceGroupLabel(code) {
  if (!code) return '—';
  const known = COMPLIANCE_GROUP_LABELS[code];
  if (known) return complianceText('compliance.grp' + code, known);
  return code;
}

export function agencyTypeLabel(type) {
  if (type == null || type === '') return '—';
  const key = Number(type);
  if (AGENCY_TYPE_LABELS[key]) return complianceText('compliance.ag' + key, AGENCY_TYPE_LABELS[key]);
  return i18n.t('compliance.typeN', { ns: 'workbench', n: type });
}

export function agencySubTypeLabel(type) {
  if (type == null || type === '') return '—';
  const key = Number(type);
  if (AGENCY_SUB_TYPE_LABELS[key]) return complianceText('compliance.sub' + key, AGENCY_SUB_TYPE_LABELS[key]);
  return i18n.t('compliance.subN', { ns: 'workbench', n: type });
}

export function agencyApplyStatusLabel(status) {
  if (status == null || status === '') return '—';
  const key = Number(status);
  if (AGENCY_APPLY_STATUS_LABELS[key]) return complianceText('compliance.ap' + key, AGENCY_APPLY_STATUS_LABELS[key]);
  return String(status);
}

export function agencyStatusLabel(status) {
  if (status == null || status === '') return '—';
  const key = Number(status);
  if (AGENCY_STATUS_LABELS[key]) return complianceText('compliance.st' + key, AGENCY_STATUS_LABELS[key]);
  return i18n.t('compliance.statusN', { ns: 'workbench', n: status });
}

export function agencyCoveredRangeLabel(range) {
  if (range == null || range === '') return '—';
  const key = Number(range);
  if (AGENCY_COVERED_RANGE_LABELS[key]) return complianceText('compliance.rg' + key, AGENCY_COVERED_RANGE_LABELS[key]);
  return String(range);
}

export function agencyOriginTypeLabel(type) {
  if (type == null || type === '') return '—';
  const key = Number(type);
  if (AGENCY_ORIGIN_TYPE_LABELS[key]) return complianceText('compliance.or' + key, AGENCY_ORIGIN_TYPE_LABELS[key]);
  return String(type);
}

export function formatAgencyValidity(row) {
  const start = row?.agencyStartTime || '';
  const end = row?.agencyEndTime || '';
  if (!start && !end) return '—';
  if (start && end) return `${start} ~ ${end}`;
  return start || end;
}

export function formatAgencyContact(row) {
  const name = row?.contactName || '';
  const phone = [row?.areaCode, row?.telephone].filter(Boolean).join(' ');
  if (name && phone) return `${name}（${phone}）`;
  return name || phone || '—';
}

export function reviewStateLabel(state) {
  if (state == null || state === '') return '—';
  const key = Number(state);
  if (REVIEW_STATE_LABELS[key]) return complianceText('compliance.rv' + key, REVIEW_STATE_LABELS[key]);
  return String(state);
}

/** Temu arbok auditStatus 同 reviewState 语义 */
export function certAuditLabel(auditStatus) {
  return reviewStateLabel(auditStatus);
}

export function reviewStateModifier(state) {
  const key = Number(state);
  if (key === 2) return 'success';
  if (key === 0 || key === 3) return 'failed';
  return 'pending';
}

export function skcAgencyReviewStateLabel(state) {
  if (state == null || state === '') return '—';
  const key = Number(state);
  if (SKC_AGENCY_REVIEW_STATE_LABELS[key]) return complianceText('compliance.sk' + key, SKC_AGENCY_REVIEW_STATE_LABELS[key]);
  return String(state);
}

export function skcAgencyReviewStateModifier(state) {
  const key = Number(state);
  if (key === 4 || key === 2) return 'success';
  if (key === 3 || key === 1) return 'failed';
  return 'pending';
}

/** 待操作：待绑定 / 绑定失败 */
export function isSkcAgencyActionNeeded(state) {
  const key = Number(state);
  return key === 1 || key === 3;
}

/** 从缓存行解析合规要求条目 */
export function extractRequirementItems(row) {
  const payload = row?.payload;
  if (payload && Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(row?.items)) return row.items;
  return [];
}

export function summarizeGroupCounts(row) {
  const summary = row?.groupSummary;
  if (summary && typeof summary === 'object') return summary;
  const items = extractRequirementItems(row);
  const out = {};
  items.forEach((item) => {
    const code = item?.complianceGroupCode || 'UNKNOWN';
    if (!out[code]) {
      out[code] = { total: 0, missingRequired: 0, missing: 0 };
    }
    out[code].total += 1;
    const review = Number(item?.reviewState);
    const required = Number(item?.isRequired) === 1;
    const action = review === 0 || review === 3;
    if (action) {
      out[code].missing += 1;
      if (required) out[code].missingRequired += 1;
    }
  });
  return out;
}

export function formatGroupSummaryText(row) {
  const summary = summarizeGroupCounts(row);
  const parts = Object.entries(summary)
    .filter(([, stats]) => (stats?.missingRequired || 0) > 0 || (stats?.missing || 0) > 0)
    .map(([code, stats]) => {
      const miss = stats.missingRequired || stats.missing || 0;
      return i18n.t('compliance.missGroup', { ns: 'workbench', name: complianceGroupLabel(code), count: miss });
    });
  if (parts.length) return parts.join(' · ');
  const missing = Number(row?.missingRequiredCount) || 0;
  if (missing > 0) return i18n.t('compliance.missRequired', { ns: 'workbench', count: missing });
  return '—';
}

/** 仅保留平台允许绑定的代理：agencyStatus=0 且 applyStatus∈{1,2} */
export function isBindableAgency(row) {
  const agencyStatus = row?.agencyStatus;
  const applyStatus = row?.applyStatus;
  if (agencyStatus != null && Number(agencyStatus) !== 0) return false;
  if (applyStatus != null) {
    const a = Number(applyStatus);
    if (a !== 1 && a !== 2) return false;
  }
  return true;
}
